import { Audio } from 'expo-av';
import {
  INTERRUPTION_MODE_ANDROID_DUCK_OTHERS,
  INTERRUPTION_MODE_IOS_DUCK_OTHERS,
  INTERRUPTION_MODE_IOS_MIX_WITH_OTHERS,
} from 'expo-av/build/Audio';
import { IAudioHelper, Logger } from 'fitbeat.react-native-components';

export class AudioHelper implements IAudioHelper {
  public static async setupAudioMode() {
    await Audio.setAudioModeAsync({
      interruptionModeAndroid: INTERRUPTION_MODE_ANDROID_DUCK_OTHERS,
      interruptionModeIOS: INTERRUPTION_MODE_IOS_DUCK_OTHERS,
      playsInSilentModeIOS: true,
      shouldDuckAndroid: false,
      staysActiveInBackground: true,
      allowsRecordingIOS: false,
      playThroughEarpieceAndroid: false,
    });
  }

  public static clearCachedItems = async (reason: string) => {
    try {
      Logger.leaveBreadcrumb(`Removing audio tip cache (${reason})`);
      for (const [key, sound] of AudioHelper.cachedSounds) {
        if (!sound) {
          return;
        }
        AudioHelper.cachedSounds.delete(key);
        await sound.unloadAsync();
      }
    } catch (e) {
      Logger.notify(e);
    }
  };

  public static getCachedSound = async (key: string) => {
    try {
      const cachedSound = AudioHelper.cachedSounds.get(key);
      if (!cachedSound) {
        return;
      }

      const cachedSoundStatus = await cachedSound.getStatusAsync();
      if (cachedSound && !cachedSoundStatus.isLoaded) {
        await cachedSound.unloadAsync();
        AudioHelper.cachedSounds.delete(key);
        return;
      }

      return cachedSound;
    } catch (e) {
      Logger.notify(e);
    }
  };

  private static cachedSounds: Map<string, Audio.Sound> = new Map<string, Audio.Sound>();

  private currentPlayingTip?: Audio.Sound | undefined;

  public async playTip(filename: string, uri: string, voiceVolume: number, cacheThisUri?: boolean) {
    try {
      const cachedSound = await AudioHelper.getCachedSound(filename);
      let sound: Audio.Sound;

      if (cachedSound) {
        Logger.leaveBreadcrumb(`Playing voice tip from cache key ${filename}`);
        sound = cachedSound;
        await sound.replayAsync();
        return;
      }
      if (this.currentPlayingTip) {
        Logger.leaveBreadcrumb(`Another tip is still playing, have to skip ${filename}`);
        return;
      }

      sound = new Audio.Sound();
      if (cacheThisUri) {
        AudioHelper.cachedSounds.set(filename, sound);
      }

      sound.setOnPlaybackStatusUpdate(async (status: any) => {
        if (status.didJustFinish && sound) {
          await sound.stopAsync();
          if (!cacheThisUri) {
            await sound.unloadAsync();
          }
          this.currentPlayingTip = undefined;
        }
      });
      await sound.loadAsync({ uri });
      await sound.setVolumeAsync(voiceVolume);
      await sound.playAsync();
      this.currentPlayingTip = sound;
    } catch (error) {
      Logger.notify(error);
    }
  }

  public async toggleCurrentPlayingTipPaused(shouldPause: boolean) {
    if (!this.currentPlayingTip) {
      return;
    }
    shouldPause ? await this.currentPlayingTip.pauseAsync() : await this.currentPlayingTip.playAsync();
  }
}
